import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from './theme';
import fieldConstants from './constants/rmaFieldConstants.json';

const API_BASE = process.env.REACT_APP_API_URL;

const ROOT_CAUSES   = (fieldConstants.rootCauses     || []).map(x => x.value);
const RESOLUTIONS   = (fieldConstants.resolutionTypes || []).map(x => x.value);
const CATEGORIES    = (fieldConstants.repairFaultCategories || []).map(x => x.value);

const CONF_STYLE = {
  high:   { color: '#4ade80', background: 'rgba(74,222,128,0.1)',  border: '1px solid rgba(74,222,128,0.35)' },
  medium: { color: '#fbbf24', background: 'rgba(251,191,36,0.1)',  border: '1px solid rgba(251,191,36,0.35)' },
  low:    { color: '#f87171', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.35)' },
};

const chip = (label, style = {}) => (
  <span style={{
    display: 'inline-block', fontSize: '0.72rem', fontWeight: 700,
    borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap',
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.05)',
    color: colors.textBody,
    ...style,
  }}>{label}</span>
);

const btn = (label, onClick, opts = {}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={opts.disabled}
    style={{
      padding: '0.35rem 0.85rem', borderRadius: 6, fontSize: '0.82rem', fontWeight: 700,
      cursor: opts.disabled ? 'not-allowed' : 'pointer', border: '1px solid',
      opacity: opts.disabled ? 0.45 : 1, transition: 'opacity 0.15s',
      borderColor: opts.danger ? 'rgba(248,113,113,0.4)' : opts.muted ? colors.panelBorder : 'rgba(70,187,137,0.45)',
      background:  opts.danger ? 'rgba(248,113,113,0.08)' : opts.muted ? 'rgba(255,255,255,0.04)' : 'rgba(70,187,137,0.12)',
      color:       opts.danger ? '#f87171' : opts.muted ? colors.textMuted : colors.primary,
      ...opts.style,
    }}
  >{label}</button>
);

function InlineSelect({ value, options, onChange }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        padding: '0.25rem 0.4rem', borderRadius: 5, fontSize: '0.78rem',
        border: `1px solid ${colors.panelBorder}`, background: '#1a2420',
        color: colors.textBody, cursor: 'pointer',
      }}
    >
      <option value="">— select —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function BackfillPage() {
  const { getToken } = useAuth();

  const [candidates, setCandidates]   = useState([]);
  const [suggestions, setSuggestions] = useState({}); // _id → suggestion
  const [accepted, setAccepted]       = useState(new Set());
  const [skipped, setSkipped]         = useState(new Set());
  const [editing, setEditing]         = useState(new Set());
  const [edits, setEdits]             = useState({}); // _id → { rootCause, resolution, repairCategories }

  const [loading, setLoading]         = useState(true);
  const [classifying, setClassifying] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [savingRows, setSavingRows]   = useState(new Set());
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState('');

  const [filter, setFilter] = useState('all'); // 'all' | 'accepted' | 'pending'

  const authFetch = useCallback(async (url, opts = {}) => {
    const token = await getToken();
    return fetch(url, { ...opts, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(opts.headers || {}) } });
  }, [getToken]);

  useEffect(() => {
    authFetch(`${API_BASE}/backfill/candidates`)
      .then(r => r.json())
      .then(d => { if (d.ok) setCandidates(d.cases); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [authFetch]);

  const [classifyProgress, setClassifyProgress] = useState({ done: 0, total: 0 });
  const [classifyingRow, setClassifyingRow] = useState(null);

  const classifyBatch = async (ids) => {
    const res = await authFetch(`${API_BASE}/backfill/classify`, {
      method: 'POST',
      body: JSON.stringify({ caseIds: ids }),
    });
    const data = await res.json();
    if (!data.ok) throw new Error(data.error);
    const map = {};
    for (const s of data.suggestions) map[s.id] = s;
    setSuggestions(prev => ({ ...prev, ...map }));
    return data.suggestions.length;
  };

  const handleClassify = async () => {
    const ids = candidates.filter(c => !suggestions[c._id]).map(c => c._id);
    if (ids.length === 0) return;
    setClassifying(true);
    setError('');
    const BATCH = 30;
    setClassifyProgress({ done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i += BATCH) {
        await classifyBatch(ids.slice(i, i + BATCH));
        setClassifyProgress(p => ({ ...p, done: Math.min(i + BATCH, ids.length) }));
      }
    } catch (err) {
      setError(`Classification failed: ${err.message}`);
    } finally {
      setClassifying(false);
      setClassifyProgress({ done: 0, total: 0 });
    }
  };

  const handleClassifyOne = async (id) => {
    setClassifyingRow(id);
    setError('');
    try {
      await classifyBatch([id]);
    } catch (err) {
      setError(`Classification failed: ${err.message}`);
    } finally {
      setClassifyingRow(null);
    }
  };

  const acceptAll = (minConf) => {
    const toAccept = new Set(accepted);
    for (const c of candidates) {
      const s = effectiveSuggestion(c._id);
      if (!s || skipped.has(c._id)) continue;
      if (minConf === 'all' || s.confidence === minConf || (minConf === 'high+' && s.confidence !== 'low')) {
        toAccept.add(c._id);
      }
    }
    setAccepted(toAccept);
  };

  const effectiveSuggestion = (id) => {
    if (edits[id]) return { ...suggestions[id], ...edits[id] };
    return suggestions[id] || null;
  };

  const handleAcceptAndSave = async (id) => {
    const s = effectiveSuggestion(id);
    if (!s) return;
    setSavingRows(prev => new Set([...prev, id]));
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/backfill/apply`, {
        method: 'POST',
        body: JSON.stringify({ updates: [{ caseId: id, rootCause: s.rootCause, resolution: s.resolution, repairCategories: s.repairCategories || [], warrantyDecision: s.warrantyDecision ?? null, repairCostEur: s.repairCostEur ?? null, repairDays: s.repairDays ?? null }] }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      setCandidates(prev => prev.filter(c => c._id !== id));
      setAccepted(prev => { const n = new Set(prev); n.delete(id); return n; });
      setResult(prev => prev ? { ...prev, saved: (prev.saved||0)+data.saved, embedded: (prev.embedded||0)+data.embedded } : data);
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setSavingRows(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const toggleSkip = (id) => {
    setSkipped(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    setAccepted(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const toggleEdit = (id) => {
    setEditing(prev => {
      const n = new Set(prev);
      if (n.has(id)) { n.delete(id); } else {
        n.add(id);
        if (!edits[id] && suggestions[id]) {
          setEdits(e => ({ ...e, [id]: { rootCause: suggestions[id].rootCause, resolution: suggestions[id].resolution, repairCategories: suggestions[id].repairCategories || [] } }));
        }
      }
      return n;
    });
  };

  const handleSave = async () => {
    const updates = [...accepted].map(id => {
      const s = effectiveSuggestion(id);
      if (!s) return null;
      return { caseId: id, rootCause: s.rootCause, resolution: s.resolution, repairCategories: s.repairCategories || [], warrantyDecision: s.warrantyDecision ?? null, repairCostEur: s.repairCostEur ?? null, repairDays: s.repairDays ?? null };
    }).filter(Boolean);

    if (updates.length === 0) return;
    setSaving(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/backfill/apply`, {
        method: 'POST',
        body: JSON.stringify({ updates }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      setResult(data);
      setCandidates(prev => prev.filter(c => !accepted.has(c._id)));
      setAccepted(new Set());
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const classified = candidates.filter(c => !!suggestions[c._id]).length;
  const highConf   = candidates.filter(c => suggestions[c._id]?.confidence === 'high').length;

  const visible = candidates.filter(c => {
    if (skipped.has(c._id)) return filter === 'all';
    if (filter === 'accepted') return accepted.has(c._id);
    if (filter === 'pending')  return !accepted.has(c._id) && !!suggestions[c._id];
    return true;
  });

  const s = {
    page:    { minHeight: '100vh', background: '#0d1612', padding: '2rem', fontFamily: 'inherit' },
    header:  { marginBottom: '1.5rem' },
    title:   { margin: 0, fontSize: '1.4rem', fontWeight: 800, color: colors.textStrong },
    sub:     { margin: '0.3rem 0 0', fontSize: '0.88rem', color: colors.textMuted },
    card:    { background: 'rgba(255,255,255,0.03)', border: `1px solid ${colors.panelBorder}`, borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem' },
    statRow: { display: 'flex', gap: '2rem', flexWrap: 'wrap', marginBottom: '1rem' },
    stat:    { display: 'flex', flexDirection: 'column' },
    statVal: { fontSize: '1.6rem', fontWeight: 800, color: colors.textStrong, lineHeight: 1 },
    statLbl: { fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.2rem' },
    table:   { width: '100%', borderCollapse: 'collapse' },
    th:      { padding: '0.6rem 0.75rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted, borderBottom: `1px solid ${colors.panelBorder}` },
    td:      { padding: '0.65rem 0.75rem', fontSize: '0.82rem', color: colors.textBody, borderBottom: `1px solid rgba(255,255,255,0.04)`, verticalAlign: 'top' },
  };

  if (loading) return <div style={s.page}><p style={{ color: colors.textMuted }}>Loading candidates…</p></div>;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>AI Backfill Tool</h1>
        <p style={s.sub}>Classify archived cases missing root cause and resolution, then embed them into Pinecone to improve AI suggestions.</p>
      </div>

      {/* Stats */}
      <div style={s.card}>
        <div style={s.statRow}>
          <div style={s.stat}><span style={s.statVal}>{candidates.length}</span><span style={s.statLbl}>Unclassified cases</span></div>
          <div style={s.stat}><span style={s.statVal}>{classified}</span><span style={s.statLbl}>AI classified</span></div>
          <div style={s.stat}><span style={{ ...s.statVal, color: '#4ade80' }}>{highConf}</span><span style={s.statLbl}>High confidence</span></div>
          <div style={s.stat}><span style={{ ...s.statVal, color: colors.primary }}>{accepted.size}</span><span style={s.statLbl}>Accepted</span></div>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {btn(
            classifying
              ? `Classifying… ${classifyProgress.done}/${classifyProgress.total}`
              : `Classify ${candidates.filter(c => !suggestions[c._id]).length} cases with AI`,
            handleClassify,
            { disabled: classifying || candidates.filter(c => !suggestions[c._id]).length === 0 }
          )}
          {classified > 0 && <>
            {btn(`Accept all high confidence (${highConf})`, () => acceptAll('high+'), { disabled: highConf === 0 })}
            {btn(`Accept all (${classified})`, () => acceptAll('all'), { muted: true })}
          </>}
          {accepted.size > 0 && btn(
            saving ? 'Saving…' : `Save & embed ${accepted.size} accepted cases`,
            handleSave,
            { disabled: saving, style: { background: 'rgba(70,187,137,0.22)', borderColor: colors.primary, color: colors.primary } }
          )}
        </div>

        <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', color: colors.textMuted }}>
          Each "Classify" call uses ~{Math.ceil(candidates.length / 30)} OpenAI API requests (~$0.01 total). Review suggestions before saving — AI classification may occasionally be incorrect.
        </p>
      </div>

      {result && (
        <div style={{ ...s.card, borderColor: 'rgba(70,187,137,0.4)', background: 'rgba(70,187,137,0.06)' }}>
          <strong style={{ color: colors.primary }}>Done.</strong>{' '}
          <span style={{ color: colors.textBody }}>{result.saved} cases saved · {result.embedded} embedded into Pinecone</span>
          {result.embedded === 0 && result.saved > 0 && (
            <span style={{ color: colors.textMuted, fontSize: '0.78rem', display: 'block', marginTop: '0.3rem' }}>
              No cases reached the completeness threshold (60) — cases with root cause + resolution + at least one of: fault categories, assessment notes, or warranty decision will be embedded.
            </span>
          )}
        </div>
      )}

      {error && <div style={{ ...s.card, borderColor: 'rgba(248,113,113,0.4)', color: '#f87171' }}>{error}</div>}

      {/* Filter tabs */}
      {classified > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {['all','pending','accepted'].map(f => (
            <button key={f} type="button" onClick={() => setFilter(f)} style={{
              padding: '0.3rem 0.9rem', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700,
              border: `1px solid ${filter === f ? colors.primary : colors.panelBorder}`,
              background: filter === f ? 'rgba(70,187,137,0.14)' : 'transparent',
              color: filter === f ? colors.primary : colors.textMuted, cursor: 'pointer',
            }}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
      )}

      {/* Table */}
      {candidates.length === 0 && !classifying && (
        <div style={{ ...s.card, textAlign: 'center', padding: '2.5rem' }}>
          <p style={{ margin: 0, color: colors.textMuted }}>All cases have been classified.</p>
        </div>
      )}

      {visible.length > 0 && (
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>RMA</th>
                <th style={s.th}>Symptom</th>
                <th style={s.th}>Root Cause</th>
                <th style={s.th}>Resolution</th>
                <th style={s.th}>Categories</th>
                <th style={s.th}>Warranty</th>
                <th style={s.th}>Cost (€)</th>
                <th style={s.th}>Days</th>
                <th style={s.th}>Conf</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(c => {
                const sugg = effectiveSuggestion(c._id);
                const isAccepted = accepted.has(c._id);
                const isSkipped  = skipped.has(c._id);
                const isEditing  = editing.has(c._id);
                const rowBg = isAccepted ? 'rgba(70,187,137,0.05)' : isSkipped ? 'rgba(0,0,0,0.2)' : 'transparent';

                return (
                  <tr key={c._id} style={{ background: rowBg, opacity: isSkipped ? 0.45 : 1 }}>
                    <td style={{ ...s.td, fontWeight: 700, color: colors.textStrong, whiteSpace: 'nowrap' }}>
                      {c.rmaId}
                      {c.productNumber && <div style={{ fontSize: '0.7rem', color: colors.textMuted, fontWeight: 400 }}>{c.productNumber}</div>}
                      {sugg?.emailCount > 0 && (
                        <div style={{ fontSize: '0.68rem', color: '#60a5fa', marginTop: '0.2rem', fontWeight: 600 }}>
                          {sugg.emailCount} email{sugg.emailCount !== 1 ? 's' : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ ...s.td, maxWidth: 220 }}>
                      <span style={{ color: colors.textBody }}>{c.description || <em style={{ color: colors.textMuted }}>No description</em>}</span>
                      {c.issueType && <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginTop: '0.2rem' }}>{c.issueType}</div>}
                    </td>

                    {/* Root cause */}
                    <td style={s.td}>
                      {isEditing ? (
                        <InlineSelect value={edits[c._id]?.rootCause || ''} options={ROOT_CAUSES} onChange={v => setEdits(e => ({ ...e, [c._id]: { ...e[c._id], rootCause: v } }))} />
                      ) : sugg ? chip(sugg.rootCause) : <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>—</span>}
                    </td>

                    {/* Resolution */}
                    <td style={s.td}>
                      {isEditing ? (
                        <InlineSelect value={edits[c._id]?.resolution || ''} options={RESOLUTIONS} onChange={v => setEdits(e => ({ ...e, [c._id]: { ...e[c._id], resolution: v } }))} />
                      ) : sugg ? chip(sugg.resolution) : <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>—</span>}
                    </td>

                    {/* Categories */}
                    <td style={s.td}>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                          {CATEGORIES.map(cat => {
                            const active = (edits[c._id]?.repairCategories || []).includes(cat);
                            return (
                              <button key={cat} type="button" onClick={() => setEdits(e => {
                                const cur = e[c._id]?.repairCategories || [];
                                return { ...e, [c._id]: { ...e[c._id], repairCategories: active ? cur.filter(x => x !== cat) : [...cur, cat] } };
                              })} style={{ padding: '1px 7px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? colors.primary : colors.panelBorder}`, background: active ? 'rgba(70,187,137,0.14)' : 'transparent', color: active ? colors.primary : colors.textMuted }}>
                                {cat}
                              </button>
                            );
                          })}
                        </div>
                      ) : sugg?.repairCategories?.length ? (
                        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                          {sugg.repairCategories.map(cat => chip(cat, { fontSize: '0.7rem' }))}
                        </div>
                      ) : <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>—</span>}
                    </td>

                    {/* Warranty */}
                    <td style={s.td}>
                      {sugg?.warrantyDecision
                        ? chip(
                            sugg.warrantyDecision === 'accepted' ? '✓ Yes' : '✗ No',
                            sugg.warrantyDecision === 'accepted'
                              ? { color: colors.primary, background: 'rgba(70,187,137,0.12)', borderColor: 'rgba(70,187,137,0.4)' }
                              : { color: '#f87171', background: 'rgba(248,113,113,0.1)', borderColor: 'rgba(248,113,113,0.4)' }
                          )
                        : <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>—</span>}
                    </td>

                    {/* Cost */}
                    <td style={s.td}>
                      {sugg?.repairCostEur != null
                        ? <span style={{ color: colors.textStrong, fontWeight: 700 }}>€{sugg.repairCostEur}</span>
                        : <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>—</span>}
                    </td>

                    {/* Duration */}
                    <td style={s.td}>
                      {sugg?.repairDays != null
                        ? <span style={{ color: colors.textBody }}>{sugg.repairDays}d</span>
                        : <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>—</span>}
                    </td>

                    {/* Confidence */}
                    <td style={s.td}>
                      {sugg ? (
                        <span style={{ ...CONF_STYLE[sugg.confidence] || CONF_STYLE.low, borderRadius: 999, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700 }}>
                          {sugg.confidence || '?'}
                        </span>
                      ) : <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>—</span>}
                    </td>

                    {/* Actions */}
                    <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {!sugg && btn(
                          classifyingRow === c._id ? '…' : 'Classify',
                          () => handleClassifyOne(c._id),
                          { disabled: classifyingRow === c._id || classifying }
                        )}
                        {sugg && !isSkipped && btn(
                          savingRows.has(c._id) ? 'Saving…' : 'Accept',
                          () => handleAcceptAndSave(c._id),
                          { disabled: savingRows.has(c._id) }
                        )}
                        {sugg && btn(isEditing ? 'Save' : 'Edit', () => {
                          if (isEditing) {
                            setEditing(prev => { const n = new Set(prev); n.delete(c._id); return n; });
                            handleAcceptAndSave(c._id);
                          } else toggleEdit(c._id);
                        }, { muted: !isEditing, disabled: savingRows.has(c._id) })}
                        {btn(isSkipped ? 'Unskip' : 'Skip', () => toggleSkip(c._id), { muted: true, danger: !isSkipped })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
